import { useMemo } from 'react';

import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { capitalize } from 'es-toolkit/compat';

import { PacingMetricStatus } from '../../schemas';

import { useColorTokens } from '@/app/themes';
import { usePlatform } from '@/features/users';
import { Tooltip } from '@/shared/components/tooltip';

const useStatusConfig = () => {
  const { steel, yellow, orange, green } = useColorTokens();
  const theme = useTheme();
  const platform = usePlatform();
  const entity = platform === 'DV360' ? 'insertion order' : 'campaign';

  return useMemo(() => {
    return {
      Learning: {
        label: 'Learning',
        description: `The algorithm has been applied but is in the initial learning phase of observing ${entity} behavior without optimization`,
        bgColor: steel[50],
        textColor: steel[700],
      },
      Testing: {
        label: 'Testing',
        description:
          'The algorithm has begun optimizing but has not yet reached an optimal state',
        bgColor: yellow[10],
        textColor: yellow[200],
      },
      Constrained: {
        label: 'Constrained',
        description: `${capitalize(entity)} constraints are preventing optimization and adjustments are needed`,
        bgColor: orange[50],
        textColor: orange[700],
      },
      Optimized: {
        label: 'Optimized',
        description: 'The algorithm has reached an optimal state',
        bgColor: green[10],
        textColor: green[200],
      },
      'N/A': {
        label: 'Not Applied',
        description: `The algorithm has not been applied to this ${entity}`,
        bgColor: theme.palette.grey[100],
        textColor: theme.palette.grey[700],
      },
    };
  }, [entity, green, orange, steel, theme.palette.grey, yellow]);
};

const PacingMetricStatusChip = ({
  status,
  starvationBidderApplied,
  disableTooltip = false,
}: {
  status: PacingMetricStatus | null;
  starvationBidderApplied: boolean;
  disableTooltip?: boolean;
}) => {
  const statusConfig = useStatusConfig();

  if (!starvationBidderApplied || !status || status === 'N/A') {
    return (
      <Typography variant='subtitleLabel' component='span'>
        Not Applied
      </Typography>
    );
  }

  if (disableTooltip) {
    return (
      <Chip
        label={
          <Typography variant='body14' component='span'>
            {statusConfig[status].label}
          </Typography>
        }
        size='small'
        sx={{
          color: statusConfig[status].textColor,
          backgroundColor: statusConfig[status].bgColor,
        }}
      />
    );
  }

  return (
    <Tooltip placement='top' title={statusConfig[status].description}>
      <Chip
        label={
          <Typography variant='body14' component='span'>
            {statusConfig[status].label}
          </Typography>
        }
        size='small'
        sx={{
          color: statusConfig[status].textColor,
          backgroundColor: statusConfig[status].bgColor,
        }}
      />
    </Tooltip>
  );
};

export default PacingMetricStatusChip;
